import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable7() {
  const pdfRef = useRef();

  const data = [
    {
      industry_label: "Nonmetallic Mineral Mining and Quarrying",
      emp_2023: 110,
      emp_2024: 88,
      difference: -22,
      pct_change: -20.0,
    },
    {
      industry_label: "Coal Mining",
      emp_2023: 0,
      emp_2024: 0,
      difference: 0,
      pct_change: 0.0,
    },
    {
      industry_label: "Oil and Gas Extraction",
      emp_2023: 43,
      emp_2024: 45,
      difference: 2,
      pct_change: 4.65,
    },
    {
      industry_label: "Metal Ore Mining",
      emp_2023: 100,
      emp_2024: 115,
      difference: 15,
      pct_change: 15.0,
    },
    {
      industry_label: "Support Activities for Mining",
      emp_2023: 196,
      emp_2024: 228,
      difference: 32,
      pct_change: 16.33,
    },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.industry_label),
    datasets: [
      {
        label: "2023 Employment",
        data: data.map((d) => d.emp_2023),
        backgroundColor: "#D8F999",
        borderRadius: 6,
      },
      {
        label: "2024 Employment",
        data: data.map((d) => d.emp_2024),
        backgroundColor: "#2C92B8",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    indexAxis: "y",
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "⛏️ Utah Mining Employment Year-over-Year Change (2023–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Number of Employees" },
        beginAtZero: true,
      },
      y: { ticks: { font: { size: 12 } } },
    },
  };

  // Insights
  const total2023 = data.reduce((sum, d) => sum + d.emp_2023, 0);
  const total2024 = data.reduce((sum, d) => sum + d.emp_2024, 0);
  const totalChange = total2024 - total2023;
  const totalPct = ((totalChange / (total2023 || 1)) * 100).toFixed(2);

  const highestGrowth = data.reduce((max, d) =>
    d.pct_change > max.pct_change ? d : max
  );

  const largestDecline = data.reduce((min, d) =>
    d.pct_change < min.pct_change ? d : min
  );

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("utah_mining_yoy_change.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-yellow-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Utah Mining Employment Change (2023 → 2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment Change
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalChange >= 0 ? "+" : ""}
              {totalChange} ({totalPct}%)
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Growth Industry
            </p>
            <p className="text-lg font-bold text-green-800">
              {highestGrowth.industry_label} ({highestGrowth.pct_change}%)
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Largest Decline</p>
            <p className="text-lg font-bold text-red-800">
              {largestDecline.industry_label} ({largestDecline.pct_change}%)
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Industry</th>
                  <th className="p-2">2023 Employment</th>
                  <th className="p-2">2024 Employment</th>
                  <th className="p-2">Change</th>
                  <th className="p-2">% Change</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.industry_label === highestGrowth.industry_label
                        ? "bg-green-100 font-semibold"
                        : row.industry_label === largestDecline.industry_label
                          ? "bg-red-100 font-semibold"
                          : "hover:bg-gray-100"
                    }`}
                    >
                      <td className="p-2">{row.industry_label}</td>
                      <td className="p-2">
                        {row.emp_2023.toLocaleString()}
                      </td>
                      <td className="p-2">
                        {row.emp_2024.toLocaleString()}
                      </td>
                      <td className="p-2">
                        {row.difference >= 0 ? "+" : ""}
                        {row.difference.toLocaleString()}
                      </td>
                      <td
                        className={`p-2 ${
                          row.pct_change > 0
                            ? "text-green-700"
                            : row.pct_change < 0
                              ? "text-red-700"
                              : "text-gray-700"
                        }`}
                      >
                        {row.pct_change}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          {/* </div> */}

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-700 mt-4">
          📊 Between 2023 and 2024, Utah’s mining sector showed moderate growth,
          with notable increases in Support Activities (+16.3%) and Metal Ore
          Mining (+15%), while Nonmetallic Mining saw a 20% decline.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
